<script setup>
// BonBoZ: trực page cần ĐÁNH DẤU CHƯA ĐỌC và CHẶN của Pancake, đặt cạnh các
// thao tác upstream (giải quyết / tắt tiếng / gửi bản ghi) chứ không thay chúng.
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

const store = useStore();
const { t } = useI18n();

const [moMenu, batMenu] = useToggle(false);
const [dangLuu, datDangLuu] = useToggle(false);
const [showEmailActionsModal, toggleEmailModal] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const lienHe = computed(() => currentChat.value?.meta?.sender || {});
const daChan = computed(() => Boolean(lienHe.value?.blocked));
const daTatTieng = computed(() => Boolean(currentChat.value?.muted));

const cacThaoTac = computed(() => [
  {
    icon: daTatTieng.value ? 'i-lucide-volume-1' : 'i-lucide-volume-off',
    label: daTatTieng.value
      ? t('COMMAND_BAR.COMMANDS.UNMUTE_CONVERSATION')
      : t('COMMAND_BAR.COMMANDS.MUTE_CONVERSATION'),
    action: daTatTieng.value ? 'unmute' : 'mute',
    value: daTatTieng.value ? 'unmute' : 'mute',
  },
  {
    icon: 'i-lucide-share',
    label: t('COMMAND_BAR.COMMANDS.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  },
  {
    icon: daChan.value ? 'i-lucide-circle-check' : 'i-lucide-ban',
    label: daChan.value
      ? t('BONBOZ.THAO_TAC.BO_CHAN')
      : t('BONBOZ.THAO_TAC.CHAN'),
    action: 'chan',
    value: 'chan',
  },
]);

const danhDauChuaDoc = async () => {
  if (!currentChat.value?.id) return;
  try {
    await store.dispatch('markMessagesUnread', { id: currentChat.value.id });
    useAlert(t('BONBOZ.THAO_TAC.DA_DANH_DAU'));
  } catch {
    useAlert(t('BONBOZ.THAO_TAC.LOI_DANH_DAU'));
  }
};

// Chặn = `contacts/update` với cột `blocked` có sẵn ở model Contact.
const doiTrangThaiChan = async () => {
  batMenu(false);
  if (!lienHe.value?.id || dangLuu.value) return;
  const muonChan = !daChan.value;
  datDangLuu(true);
  try {
    await store.dispatch('contacts/update', {
      ...lienHe.value,
      blocked: muonChan,
    });
    useAlert(
      muonChan ? t('BONBOZ.THAO_TAC.DA_CHAN') : t('BONBOZ.THAO_TAC.DA_BO_CHAN')
    );
  } catch {
    useAlert(t('BONBOZ.THAO_TAC.LOI_CHAN'));
  } finally {
    datDangLuu(false);
  }
};

// Tắt tiếng là mức hội thoại, khác hẳn Chặn ở trên vốn là mức liên hệ.
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('COMMAND_BAR.COMMANDS.MUTE_CONVERSATION'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('COMMAND_BAR.COMMANDS.UNMUTE_CONVERSATION'));
};

const bamThaoTac = ({ action }) => {
  batMenu(false);
  if (action === 'chan') doiTrangThaiChan();
  else if (action === 'mute') mute();
  else if (action === 'unmute') unmute();
  else if (action === 'send_transcript') toggleEmailModal();
};

// Ô lệnh Ctrl+K vẫn emit ba lệnh này; đây là nơi duy nhất nghe chúng.
useEmitter(CMD_MUTE_CONVERSATION, mute);
useEmitter(CMD_UNMUTE_CONVERSATION, unmute);
useEmitter(CMD_SEND_TRANSCRIPT, toggleEmailModal);
</script>

<template>
  <div class="relative flex items-center gap-1 actions--container">
    <!-- BonBoZ: phong bì có chấm = chưa đọc, đúng icon Pancake dùng ở header. -->
    <ButtonV4
      v-tooltip="$t('BONBOZ.THAO_TAC.CHUA_DOC')"
      size="sm"
      variant="ghost"
      color="slate"
      icon="i-lucide-mail-plus"
      :aria-label="$t('BONBOZ.THAO_TAC.CHUA_DOC')"
      class="rounded-md hover:bg-n-alpha-2"
      @click="danhDauChuaDoc"
    />
    <!-- BonBoZ: bỏ nút trạng thái hội thoại (Giải quyết / Chờ khách / Báo lại)
         theo yêu cầu 23/08/2026 — chưa dùng tới. Bỏ luôn ở menu chuột phải bên
         cột hội thoại; để một chỗ mà bỏ chỗ kia thì người dùng đổi được trạng
         thái ở nơi này nhưng không thấy nó ở nơi khác. -->
    <div v-on-click-outside="() => batMenu(false)" class="relative flex group">
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        :aria-label="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="batMenu()"
      />
      <DropdownMenu
        v-if="moMenu"
        :menu-items="cacThaoTac"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="bamThaoTac"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
